import React, { useMemo, useState } from 'react';
import { TopicFrameworkCard } from './TopicFrameworkCard';
import { TopicFramework, TopicFrameworkTopic } from './types';
import Badge from '../Badge';
import Button from '../Button';

interface TopicFrameworkDisplayProps {
  topicFramework: TopicFramework;
  className?: string;
}

interface EntityMetadataPanelProps {
  metadata?: TopicFramework['entity_metadata'];
}

const EntityMetadataPanel: React.FC<EntityMetadataPanelProps> = ({ metadata }) => {
  if (!metadata || (!metadata.detected_locations?.length && !metadata.detected_organizations?.length && !metadata.detected_products?.length)) {
    return null;
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
      <h3 className="font-medium text-gray-900 dark:text-gray-100">
        Detected Entities
      </h3>
      
      {metadata.detected_locations && metadata.detected_locations.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Locations ({metadata.detected_locations.length})
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {metadata.detected_locations.map((location, index) => (
              <Badge
                key={index}
                text={`📍 ${location}`}
                variant="success"
                size="sm"
                className="bg-green-50 text-green-700 border-green-200"
              />
            ))}
          </div>
        </div>
      )}

      {metadata.detected_organizations && metadata.detected_organizations.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Organizations ({metadata.detected_organizations.length})
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {metadata.detected_organizations.map((org, index) => (
              <Badge
                key={index}
                text={`🏢 ${org}`}
                variant="info"
                size="sm"
                className="bg-blue-50 text-blue-700 border-blue-200"
              />
            ))}
          </div>
        </div>
      )}

      {metadata.detected_products && metadata.detected_products.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Products ({metadata.detected_products.length})
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {metadata.detected_products.map((product, index) => (
              <Badge
                key={index}
                text={`📦 ${product}`}
                variant="primary"
                size="sm"
                className="bg-purple-50 text-purple-700 border-purple-200"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const TopicFrameworkDisplay: React.FC<TopicFrameworkDisplayProps> = ({
  topicFramework,
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showEntityMetadata, setShowEntityMetadata] = useState(false);
  const [sortBy, setSortBy] = useState<'id' | 'name' | 'examples'>('id');

  // Parse and process topic framework data
  const processedTopics = useMemo(() => {
    if (!topicFramework?.topics) return [];
    
    let topics: TopicFrameworkTopic[] = [];
    
    // Handle both array and object formats
    if (Array.isArray(topicFramework.topics)) {
      topics = topicFramework.topics;
    } else {
      // Convert object format to array
      topics = Object.values(topicFramework.topics);
    }

    return topics;
  }, [topicFramework]);

  // Filter and sort topics
  const filteredAndSortedTopics = useMemo(() => {
    let filtered = processedTopics.filter(topic =>
      topic.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      topic.definition.toLowerCase().includes(searchTerm.toLowerCase()) ||
      topic.examples?.some(example => 
        example.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    // Sort topics
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'examples':
          return (b.examples?.length || 0) - (a.examples?.length || 0);
        case 'id':
        default:
          return a.topic_id - b.topic_id;
      }
    });

    return filtered;
  }, [processedTopics, searchTerm, sortBy]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalSubtopics = processedTopics.reduce((sum, topic) => sum + (topic.subtopics?.length || 0), 0);
    const totalExamples = processedTopics.reduce((sum, topic) => sum + (topic.examples?.length || 0), 0);
    const module1Topics = processedTopics.filter(t => t.source_module === 'module1').length;
    const module2Topics = processedTopics.filter(t => t.source_module === 'module2').length;

    return {
      totalTopics: processedTopics.length,
      totalSubtopics,
      totalExamples,
      module1Topics,
      module2Topics
    };
  }, [processedTopics]);

  if (!topicFramework || !processedTopics.length) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <p className="text-gray-500 dark:text-gray-400">
          No topic framework data available.
        </p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with metadata */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Topic Framework
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Version {topicFramework.version} • Created {new Date(topicFramework.created_at).toLocaleDateString()}
            </p>
          </div>
          
          {topicFramework.entity_metadata && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEntityMetadata(!showEntityMetadata)}
            >
              {showEntityMetadata ? 'Hide' : 'Show'} Entity Metadata
            </Button>
          )}
        </div>

        {/* Statistics */}
        <div className="flex flex-wrap gap-2">
          <Badge text={`${stats.totalTopics} Topics`} variant="default" size="sm" />
          <Badge text={`${stats.totalSubtopics} Subtopics`} variant="default" size="sm" />
          <Badge text={`${stats.totalExamples} Examples`} variant="default" size="sm" />
          {stats.module1Topics > 0 && (
            <Badge 
              text={`${stats.module1Topics} from Module 1`}
              variant="success"
              size="sm"
              className="bg-green-100 text-green-800 border-green-300"
            />
          )}
          {stats.module2Topics > 0 && (
            <Badge 
              text={`${stats.module2Topics} from Module 2`}
              variant="primary"
              size="sm"
              className="bg-purple-100 text-purple-800 border-purple-300"
            />
          )}
        </div>

        {/* Entity Metadata Panel */}
        {showEntityMetadata && (
          <EntityMetadataPanel metadata={topicFramework.entity_metadata} />
        )}
      </div>

      {/* Search and Sort Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search topics, definitions, or examples..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'id' | 'name' | 'examples')}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="id">Sort by ID</option>
            <option value="name">Sort by Name</option>
            <option value="examples">Sort by Examples Count</option>
          </select>
        </div>
      </div>

      {/* Results Summary */}
      {searchTerm && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Showing {filteredAndSortedTopics.length} of {processedTopics.length} topics
        </p>
      )}

      {/* Topics Grid */}
      {filteredAndSortedTopics.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredAndSortedTopics.map((topic) => (
            <TopicFrameworkCard
              key={topic.topic_id}
              topic={topic}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">
            No topics match your search criteria.
          </p>
        </div>
      )}
    </div>
  );
};